import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Box,
  Typography,
  Button,
  Paper,
  InputAdornment,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  Close,
  Search,
  LocationOn,
  CheckCircle,
  DragIndicator,
  MyLocation,
} from "@mui/icons-material";
import { GoogleMap, Marker, Autocomplete, LoadScript } from "@react-google-maps/api";
import { getGeocode, getLatLng } from "use-places-autocomplete";

const libraries: ("places")[] = ["places"];

// Type definitions
interface AddressComponents {
  streetNumber?: string;
  route?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  neighborhood?: string;
}

interface LocationDetails {
  formattedAddress: string;
  lat: number;
  lng: number;
  placeId: string;
  addressComponents: AddressComponents;
}

interface MapLocationPickerProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (location: LocationDetails) => void;
  initialAddress?: string;
  apiKey?: string;
}

interface GoogleMapsWindow extends Window {
  google?: {
    maps: {
      places: {
        AutocompleteService: new () => google.maps.places.AutocompleteService;
        PlacesService: new (element: HTMLElement) => google.maps.places.PlacesService;
        PlacesServiceStatus: {
          OK: string;
        };
      };
      Geocoder: new () => google.maps.Geocoder;
      LatLng: new (lat: number, lng: number) => google.maps.LatLng;
      Size: new (width: number, height: number) => google.maps.Size;
      Point: new (x: number, y: number) => google.maps.Point;
      Animation: {
        DROP: google.maps.Animation;
      };
      Map: new (element: HTMLElement, options?: google.maps.MapOptions) => google.maps.Map;
      event: {
        clearInstanceListeners: (instance: any) => void;
      };
    };
  };
}

interface AutocompletePrediction {
  place_id: string;
  description: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

interface LatLng {
  lat: number;
  lng: number;
}

const MapLocationPicker: React.FC<MapLocationPickerProps> = ({ 
  open, 
  onClose, 
  onConfirm, 
  initialAddress = "", 
  apiKey 
}) => {
  console.log("🟠 [MapLocationPicker] Component rendered");
  console.log("🟠 [MapLocationPicker] Props received:");
  console.log("🟠 [MapLocationPicker] - open:", open);
  console.log("🟠 [MapLocationPicker] - initialAddress:", initialAddress);
  console.log("🟠 [MapLocationPicker] - apiKey:", apiKey ? "Present" : "Missing");
  console.log("🟠 [MapLocationPicker] - onClose:", typeof onClose);
  console.log("🟠 [MapLocationPicker] - onConfirm:", typeof onConfirm);
  
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<LocationDetails | null>(null);
  const [searchValue, setSearchValue] = useState<string>(initialAddress);
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [mapCenter, setMapCenter] = useState<LatLng>({ lat: 37.7749, lng: -122.4194 }); // Default: San Francisco
  const [markerPosition, setMarkerPosition] = useState<LatLng | null>(null);
  const [locationDetails, setLocationDetails] = useState<LocationDetails | null>(null);
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const [showPredictions, setShowPredictions] = useState<boolean>(false);
  const [isLocating, setIsLocating] = useState<boolean>(false);
  const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const searchInputRef = useRef<HTMLDivElement | null>(null);

  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const googleMapsApiKey = apiKey || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyBvJpMkcmHP3YsK3En2HwqpfxOCeL6RoEo";
  const googleWindow = window as unknown as GoogleMapsWindow;

  const extractAddressComponents = useCallback(
    (addressComponents: google.maps.GeocoderAddressComponent[] | undefined | null) => {
      const comps = addressComponents || [];
      return comps.reduce((acc: AddressComponents, component: google.maps.GeocoderAddressComponent) => {
        const types = component.types || [];
        if (types.includes("street_number")) acc.streetNumber = component.long_name;
        if (types.includes("route")) acc.route = component.long_name;
        if (types.includes("locality")) acc.city = component.long_name;
        // Some locations use postal_town instead of locality
        if (!acc.city && types.includes("postal_town")) acc.city = component.long_name;
        // Some locations use administrative_area_level_2 as city/district
        if (!acc.city && types.includes("administrative_area_level_2")) acc.city = component.long_name;
        if (types.includes("administrative_area_level_1")) acc.state = component.long_name;
        if (types.includes("country")) acc.country = component.long_name;
        if (types.includes("postal_code")) acc.postalCode = component.long_name;
        if (types.includes("sublocality_level_1") || types.includes("neighborhood")) {
          acc.neighborhood = component.long_name;
        }
        return acc;
      }, {});
    },
    []
  );

  const reverseGeocodeLatLngWithNominatim = useCallback(async (pos: LatLng): Promise<LocationDetails | null> => {
    try {
      // Public OSM reverse geocoder (no key). Used only as fallback when Google reverse geocode fails.
      const url =
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(pos.lat)}` +
        `&lon=${encodeURIComponent(pos.lng)}&addressdetails=1`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          // Nominatim recommends sending a valid UA via server. In browser we keep it minimal.
          "Accept": "application/json",
        },
      });
      if (!res.ok) return null;
      const data: any = await res.json();
      const addr = data?.address || {};

      const city =
        addr.city ||
        addr.town ||
        addr.village ||
        addr.suburb ||
        addr.county ||
        addr.state_district ||
        addr.municipality ||
        undefined;

      const formattedAddress: string =
        data?.display_name ||
        [addr.road, city, addr.state, addr.country].filter(Boolean).join(", ") ||
        "";

      const details: LocationDetails = {
        formattedAddress,
        lat: pos.lat,
        lng: pos.lng,
        placeId: String(data?.place_id || ""),
        addressComponents: {
          streetNumber: addr.house_number,
          route: addr.road,
          city,
          state: addr.state,
          country: addr.country,
          postalCode: addr.postcode,
          neighborhood: addr.neighbourhood || addr.suburb,
        },
      };

      return details;
    } catch (e) {
      return null;
    }
  }, []);

  const reverseGeocodeLatLng = useCallback(
    async (pos: LatLng): Promise<LocationDetails> => {
      // Prefer Google Maps JS Geocoder (works well for lat/lng -> address components)
      if (googleWindow.google?.maps) {
        const geocoder = new googleWindow.google.maps.Geocoder();
        const geocodeRes = await geocoder.geocode({ location: pos });
        const best = geocodeRes?.results?.[0];
        if (best) {
          const details: LocationDetails = {
            formattedAddress: best.formatted_address || "",
            lat: pos.lat,
            lng: pos.lng,
            placeId: best.place_id || "",
            addressComponents: extractAddressComponents(best.address_components || []),
          };
          // If we got a result but no meaningful address, treat as failure
          if (!details.formattedAddress && Object.keys(details.addressComponents || {}).length === 0) {
            throw new Error("Empty geocoder result");
          }
          return details;
        }
      }

      // Fallback 1: Nominatim (lat/lng based, usually accurate for city/state/country)
      const osm = await reverseGeocodeLatLngWithNominatim(pos);
      if (osm) return osm;

      // Fallback to use-places-autocomplete helper (kept for compatibility)
      const results = await getGeocode({ location: pos });
      if (results && results[0]) {
        const place = results[0];
        return {
          formattedAddress: place.formatted_address || "",
          lat: pos.lat,
          lng: pos.lng,
          placeId: place.place_id || "",
          addressComponents: extractAddressComponents(place.address_components || []),
        };
      }

      throw new Error("No geocode results found");
    },
    [extractAddressComponents, googleWindow.google, reverseGeocodeLatLngWithNominatim]
  );
  
  // Log when open prop changes
  useEffect(() => {
    console.log("🟠 [MapLocationPicker] open prop changed to:", open);
    console.log("🟠 [MapLocationPicker] Component state:");
    console.log("🟠 [MapLocationPicker] - isLoaded:", isLoaded);
    console.log("🟠 [MapLocationPicker] - loadError:", loadError);
    console.log("🟠 [MapLocationPicker] - markerPosition:", markerPosition);
    console.log("🟠 [MapLocationPicker] - selectedLocation:", selectedLocation);
  }, [open, isLoaded, loadError]);

  // Initialize autocomplete service when Google Maps is loaded
  useEffect(() => {
    const googleWindow = window as unknown as GoogleMapsWindow;
    if (isLoaded && googleWindow.google?.maps?.places) {
      const service = new googleWindow.google.maps.places.AutocompleteService();
      setAutocompleteService(service);
      console.log("🟠 [MapLocationPicker] AutocompleteService initialized");
    }
  }, [isLoaded]);

  // Initialize with initial address if provided
  useEffect(() => {
    if (initialAddress && open && isLoaded && !markerPosition) {
      handleGeocode(initialAddress);
    }
  }, [initialAddress, open, isLoaded]);

  // Get autocomplete predictions
  const getPredictions = useCallback(async (input: string) => {
    if (!input || input.length < 3) {
      setPredictions([]);
      setShowPredictions(false);
      return;
    }

    if (!autocompleteService) {
      console.warn("🟠 [MapLocationPicker] AutocompleteService not available");
      return;
    }

    const googleWindow = window as unknown as GoogleMapsWindow;
    if (!googleWindow.google?.maps?.places) {
      return;
    }

    try {
      autocompleteService.getPlacePredictions(
        {
          input: input,
          types: ['geocode', 'establishment'],
          componentRestrictions: { country: ['in', 'us', 'gb'] },
        },
        (predictions: google.maps.places.AutocompletePrediction[] | null, status: google.maps.places.PlacesServiceStatus) => {
          // Avoid runtime crashes during hot reload / load races by not dereferencing
          // google.maps.places.PlacesServiceStatus here. `status` is a string enum and "OK" is the success value.
          if (String(status) === "OK" && predictions) {
            console.log("🟠 [MapLocationPicker] Predictions received:", predictions.length);
            setPredictions(predictions as AutocompletePrediction[]);
            setShowPredictions(true);
          } else {
            console.log("🟠 [MapLocationPicker] No predictions or error:", status);
            setPredictions([]);
            setShowPredictions(false);
          }
        }
      );
    } catch (error) {
      console.error("🟠 [MapLocationPicker] Error getting predictions:", error);
      setPredictions([]);
      setShowPredictions(false);
    }
  }, [autocompleteService]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue && searchValue.length >= 3) {
        getPredictions(searchValue);
      } else {
        setPredictions([]);
        setShowPredictions(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue, getPredictions]);

  // Close predictions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target as Node)) {
        setShowPredictions(false);
      }
    };

    if (showPredictions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showPredictions]);

  const handleGeocode = async (address: string) => {
    if (!address) return;
    setIsLoading(true);
    try {
      const results = await getGeocode({ address });
      if (results && results[0]) {
        const location = await getLatLng(results[0]);
        const place = results[0];
        
        setMapCenter(location);
        setMarkerPosition(location);
        
        const formattedAddress = place.formatted_address || address;
        
        const details: LocationDetails = {
          formattedAddress,
          lat: location.lat,
          lng: location.lng,
          placeId: place.place_id,
          addressComponents: extractAddressComponents(place.address_components || []),
        };
        
        setLocationDetails(details);
        setSelectedLocation(details);
      }
    } catch (error) {
      console.error("Geocoding error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onPlaceChanged = useCallback(() => {
    console.log("🟠 [MapLocationPicker] onPlaceChanged called");
    // Hide predictions when place is selected
    setShowPredictions(false);
    setPredictions([]);
    
    if (autocomplete) {
      const place = autocomplete.getPlace();
      console.log("🟠 [MapLocationPicker] Place selected:", place);
      
      if (!place.geometry) {
        console.warn("🟠 [MapLocationPicker] Place has no geometry");
        return;
      }
      
      const location: LatLng = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      
      console.log("🟠 [MapLocationPicker] Location:", location);
      
      setMapCenter(location);
      setMarkerPosition(location);
      setSearchValue(place.formatted_address || place.name || "");
      
      // Extract address components
      const addressComponents = place.address_components || [];
      const details: LocationDetails = {
        formattedAddress: place.formatted_address || place.name || "",
        lat: location.lat,
        lng: location.lng,
        placeId: place.place_id,
        addressComponents: addressComponents.reduce((acc: AddressComponents, component: google.maps.places.PlaceResult["address_components"][0]) => {
          if (!component) return acc;
          const types = component.types;
          if (types.includes("street_number")) acc.streetNumber = component.long_name;
          if (types.includes("route")) acc.route = component.long_name;
          if (types.includes("locality")) acc.city = component.long_name;
          if (types.includes("administrative_area_level_1")) acc.state = component.long_name;
          if (types.includes("country")) acc.country = component.long_name;
          if (types.includes("postal_code")) acc.postalCode = component.long_name;
          if (types.includes("sublocality_level_1") || types.includes("neighborhood")) {
            acc.neighborhood = component.long_name;
          }
          return acc;
        }, {}),
      };
      
      console.log("🟠 [MapLocationPicker] Location details:", details);
      setLocationDetails(details);
      setSelectedLocation(details);
    } else {
      console.warn("🟠 [MapLocationPicker] Autocomplete is null");
    }
  }, [autocomplete]);

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const onMarkerDragEnd = useCallback(async (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    
    console.log("🟠 [MapLocationPicker] Marker dragged to:", e.latLng);
    const newPosition: LatLng = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };
    
    console.log("🟠 [MapLocationPicker] New position:", newPosition);
    
    // Update marker position immediately
    setMarkerPosition(newPosition);
    // Update map center to follow marker
    setMapCenter(newPosition);
    setIsLoading(true);
    
    try {
      const details = await reverseGeocodeLatLng(newPosition);
      console.log("🟠 [MapLocationPicker] Reverse geocoded details:", details);
      setSearchValue(details.formattedAddress || `${newPosition.lat.toFixed(4)}, ${newPosition.lng.toFixed(4)}`);
      setLocationDetails(details);
      setSelectedLocation(details);
    } catch (error) {
      console.error("🔴 [MapLocationPicker] Reverse geocoding error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [reverseGeocodeLatLng]);

  const handleConfirm = () => {
    if (selectedLocation && onConfirm) {
      onConfirm(selectedLocation);
    }
    onClose();
  };

  const handleClose = () => {
    setSelectedLocation(null);
    setLocationDetails(null);
    setMarkerPosition(null);
    setSearchValue(initialAddress);
    onClose();
  };

  const handleCoordinatesRetrieved = async (latitude: number, longitude: number, fallbackData?: any) => {
    const newPosition: LatLng = { lat: latitude, lng: longitude };
    
    setMapCenter(newPosition);
    setMarkerPosition(newPosition);
    
    try {
      const details = await reverseGeocodeLatLng(newPosition);
      // Enrich missing pieces from fallback data if needed
      const mergedComponents: AddressComponents = { ...(details.addressComponents || {}) };
      if (fallbackData) {
        if (!mergedComponents.city && fallbackData.city) mergedComponents.city = fallbackData.city;
        if (!mergedComponents.state && fallbackData.region) mergedComponents.state = fallbackData.region;
        if (!mergedComponents.country && fallbackData.country_name) mergedComponents.country = fallbackData.country_name;
        if (!mergedComponents.postalCode && fallbackData.postal) mergedComponents.postalCode = fallbackData.postal;
      }
      const merged: LocationDetails = {
        ...details,
        addressComponents: mergedComponents,
        formattedAddress:
          details.formattedAddress ||
          (fallbackData
            ? [fallbackData.city, fallbackData.region, fallbackData.country_name].filter(Boolean).join(", ")
            : "") ||
          `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
      };
      setSearchValue(merged.formattedAddress || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
      setLocationDetails(merged);
      setSelectedLocation(merged);
    } catch (error) {
      console.error("Reverse geocoding error:", error);
      
      let formattedAddress = `Coordinates: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
      let addressComponents: any = {};
      
      // If we have IP fallback data, use it to construct a meaningful address!
      if (fallbackData) {
        const parts = [];
        if (fallbackData.city) { parts.push(fallbackData.city); addressComponents.city = fallbackData.city; }
        if (fallbackData.region) { parts.push(fallbackData.region); addressComponents.state = fallbackData.region; }
        if (fallbackData.country_name) { parts.push(fallbackData.country_name); addressComponents.country = fallbackData.country_name; }
        if (fallbackData.postal) { addressComponents.postalCode = fallbackData.postal; }
        
        if (parts.length > 0) {
          formattedAddress = parts.join(", ");
        }
      }

      setSearchValue(formattedAddress);
      const fallbackDetails: LocationDetails = {
        formattedAddress,
        lat: latitude,
        lng: longitude,
        placeId: "",
        addressComponents: addressComponents,
      };
      setLocationDetails(fallbackDetails);
      setSelectedLocation(fallbackDetails);
    } finally {
      setIsLocating(false);
    }
  };

  const getCurrentLocation = () => {
    setIsLocating(true);

    const fetchIpLocation = async () => {
      try {
        const response = await fetch("https://ipapi.co/json/");
        const data = await response.json();
        if (data && data.latitude && data.longitude) {
          await handleCoordinatesRetrieved(data.latitude, data.longitude, data);
          return;
        }
      } catch (err) {
        setIsLocating(false);
      }
    };

    // Fetch IP enrichment only as a LAST resort.
    const fetchIpEnrichmentOnly = async () => {
      try {
        const response = await fetch("https://ipapi.co/json/");
        const data = await response.json();
        return data && typeof data === "object" ? data : null;
      } catch (e) {
        return null;
      }
    };

    if (!navigator.geolocation) {
      fetchIpLocation();
      return;
    }

    // Fallback timeout in case the browser silently drops the geolocation request
    let fallbackTriggered = false;
    const fallbackTimeout = setTimeout(() => {
      fallbackTriggered = true;
      fetchIpLocation();
    }, 20000);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        if (fallbackTriggered) return;
        clearTimeout(fallbackTimeout);
        // Prefer reverse geocode from actual coordinates; use IP only if needed.
        const enrichment = await fetchIpEnrichmentOnly();
        await handleCoordinatesRetrieved(
          position.coords.latitude,
          position.coords.longitude,
          enrichment || undefined
        );
      },
      (error) => {
        if (fallbackTriggered) return;
        clearTimeout(fallbackTimeout);
        fetchIpLocation();
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  };

  return (
    <LoadScript
      googleMapsApiKey={googleMapsApiKey}
      libraries={libraries}
      onLoad={() => {
        console.log("🟠 [MapLocationPicker] Google Maps script loaded successfully");
        setIsLoaded(true);
      }}
      onError={(error: Error) => {
        console.error("🔴 [MapLocationPicker] Google Maps load error:", error);
        setLoadError(error);
      }}
    >
      <Dialog
        open={open}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleClose();
        }}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: 1,
            borderColor: "divider",
            pb: 2,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Select Location on Map
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: "flex", height: "calc(90vh - 80px)" }}>
          {loadError ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
                p: 3,
              }}
            >
              <Typography color="error" variant="h6" sx={{ mb: 2 }}>
                Error Loading Map
              </Typography>
              <Typography color="error">
                Failed to load Google Maps. Please check your API key.
              </Typography>
              <Button onClick={handleClose} sx={{ mt: 2 }}>
                Close
              </Button>
            </Box>
          ) : !isLoaded ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Left Sidebar - Search */}
              <Box
                sx={{
                  width: 400,
                  borderRight: 1,
                  borderColor: "divider",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: "#f5f5f5",
                }}
              >
                {/* Search Section */}
                <Box ref={searchInputRef} sx={{ p: 2, borderBottom: 1, borderColor: "divider", bgcolor: "white", position: "relative" }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Search Address
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={isLocating ? <CircularProgress size={20} /> : <MyLocation />}
                    onClick={getCurrentLocation}
                    disabled={isLocating || !isLoaded}
                    fullWidth
                    sx={{ mb: 2, textTransform: 'none', borderRadius: 2 }}
                  >
                    {isLocating ? "Getting Location..." : !isLoaded ? "Map Loading..." : "Get Current Location"}
                  </Button>
                  <Autocomplete
                    onLoad={(autocomplete: google.maps.places.Autocomplete) => {
                      console.log("🟠 [MapLocationPicker] Autocomplete loaded:", autocomplete);
                      setAutocomplete(autocomplete);
                      autocompleteRef.current = autocomplete;
                      
                      // Set component restrictions for better results
                      if (autocomplete && autocomplete.setComponentRestrictions) {
                        autocomplete.setComponentRestrictions({ country: ['in', 'us', 'gb'] });
                      }
                    }}
                    onPlaceChanged={onPlaceChanged}
                    options={{
                      types: ["geocode", "establishment"],
                      fields: [
                        "address_components",
                        "geometry",
                        "formatted_address",
                        "place_id",
                        "name",
                        "types",
                      ],
                      componentRestrictions: { country: ['in', 'us', 'gb'] },
                    }}
                    restrictions={{
                      country: ['in', 'us', 'gb'],
                    }}
                  >
                    <TextField
                      fullWidth
                      placeholder="Search for an address (e.g., Chandigarh, Delhi)..."
                      value={searchValue}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const newValue = e.target.value;
                        console.log("🟠 [MapLocationPicker] Search value changed:", newValue);
                        setSearchValue(newValue);
                        // Clear selection when user starts typing again
                        if (selectedLocation && newValue !== selectedLocation.formattedAddress) {
                          setSelectedLocation(null);
                          setLocationDetails(null);
                          setMarkerPosition(null);
                        }
                      }}
                      onFocus={() => {
                        // Show predictions when focused
                        if (searchValue && searchValue.length >= 3) {
                          getPredictions(searchValue);
                        }
                      }}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          console.log("🟠 [MapLocationPicker] Enter pressed, triggering search");
                          if (autocompleteRef.current && searchValue && googleWindow.google?.maps?.places) {
                            // Trigger autocomplete search
                            const placesService = new googleWindow.google.maps.places.PlacesService(document.createElement('div'));
                            placesService.textSearch(
                              {
                                query: searchValue,
                              },
                              (results: google.maps.places.PlaceResult[] | null, status: google.maps.places.PlacesServiceStatus) => {
                                if (String(status) === "OK" && results && results[0]) {
                                  const place = results[0];
                                  if (place.geometry?.location) {
                                    const location: LatLng = {
                                      lat: place.geometry.location.lat(),
                                      lng: place.geometry.location.lng(),
                                    };
                                    setMapCenter(location);
                                    setMarkerPosition(location);
                                    setSearchValue(place.formatted_address || place.name || "");
                                    
                                    const addressComponents = place.address_components || [];
                                    const details: LocationDetails = {
                                      formattedAddress: place.formatted_address || place.name || "",
                                      lat: location.lat,
                                      lng: location.lng,
                                      placeId: place.place_id || "",
                                      addressComponents: addressComponents.reduce((acc: AddressComponents, component: google.maps.places.PlaceResult["address_components"][0]) => {
                                        if (!component) return acc;
                                        const types = component.types;
                                        if (types.includes("street_number")) acc.streetNumber = component.long_name;
                                        if (types.includes("route")) acc.route = component.long_name;
                                        if (types.includes("locality")) acc.city = component.long_name;
                                        if (types.includes("administrative_area_level_1")) acc.state = component.long_name;
                                        if (types.includes("country")) acc.country = component.long_name;
                                        if (types.includes("postal_code")) acc.postalCode = component.long_name;
                                        if (types.includes("sublocality_level_1") || types.includes("neighborhood")) {
                                          acc.neighborhood = component.long_name;
                                        }
                                        return acc;
                                      }, {}),
                                    };
                                    setLocationDetails(details);
                                    setSelectedLocation(details);
                                  }
                                }
                              }
                            );
                          }
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                        endAdornment: isLoading && <CircularProgress size={20} />,
                      }}
                      sx={{ bgcolor: "white" }}
                    />
                  </Autocomplete>
                  {searchValue && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                      Type to see suggestions, or press Enter to search
                    </Typography>
                  )}
                  
                  {/* Autocomplete Suggestions Dropdown */}
                  {showPredictions && predictions.length > 0 && (
                    <Paper
                      elevation={3}
                      sx={{
                        position: "absolute",
                        zIndex: 1300,
                        mt: 0.5,
                        maxHeight: 300,
                        overflow: "auto",
                        width: "calc(100% - 32px)",
                        bgcolor: "white",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      {predictions.map((prediction, index) => (
                        <Box
                          key={prediction.place_id}
                          onClick={async () => {
                            console.log("🟠 [MapLocationPicker] Prediction selected:", prediction);
                            // Hide predictions dropdown immediately
                            setShowPredictions(false);
                            setPredictions([]);
                            setSearchValue(prediction.description);
                            
                            // Get place details
                            setIsLoading(true);
                            try {
                              if (googleWindow.google?.maps?.places) {
                                const service = new googleWindow.google.maps.places.PlacesService(document.createElement('div'));
                                service.getDetails(
                                  {
                                    placeId: prediction.place_id,
                                    fields: ['geometry', 'formatted_address', 'address_components', 'place_id', 'name'],
                                  },
                                  (place: google.maps.places.PlaceResult | null, status: google.maps.places.PlacesServiceStatus) => {
                                    if (String(status) === "OK" && place && place.geometry?.location) {
                                      const location: LatLng = {
                                        lat: place.geometry.location.lat(),
                                        lng: place.geometry.location.lng(),
                                      };
                                      
                                      setMapCenter(location);
                                      setMarkerPosition(location);
                                      
                                      const addressComponents = place.address_components || [];
                                      const details: LocationDetails = {
                                        formattedAddress: place.formatted_address || place.name || "",
                                        lat: location.lat,
                                        lng: location.lng,
                                        placeId: place.place_id || "",
                                        addressComponents: addressComponents.reduce((acc: AddressComponents, component: google.maps.places.PlaceResult["address_components"][0]) => {
                                          if (!component) return acc;
                                          const types = component.types;
                                          if (types.includes("street_number")) acc.streetNumber = component.long_name;
                                          if (types.includes("route")) acc.route = component.long_name;
                                          if (types.includes("locality")) acc.city = component.long_name;
                                          if (types.includes("administrative_area_level_1")) acc.state = component.long_name;
                                          if (types.includes("country")) acc.country = component.long_name;
                                          if (types.includes("postal_code")) acc.postalCode = component.long_name;
                                          if (types.includes("sublocality_level_1") || types.includes("neighborhood")) {
                                            acc.neighborhood = component.long_name;
                                          }
                                          return acc;
                                        }, {}),
                                      };
                                      
                                      setLocationDetails(details);
                                      setSelectedLocation(details);
                                    }
                                    setIsLoading(false);
                                  }
                                );
                              }
                            } catch (error) {
                              console.error("🟠 [MapLocationPicker] Error getting place details:", error);
                              setIsLoading(false);
                            }
                          }}
                          sx={{
                            p: 1.5,
                            cursor: "pointer",
                            borderBottom: index < predictions.length - 1 ? 1 : 0,
                            borderColor: "divider",
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                            <LocationOn sx={{ fontSize: 20, color: "primary.main", mt: 0.5, flexShrink: 0 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={500} noWrap>
                                {prediction.structured_formatting?.main_text || prediction.description}
                              </Typography>
                              {prediction.structured_formatting?.secondary_text && (
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {prediction.structured_formatting.secondary_text}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Paper>
                  )}
                </Box>

              {/* Selected Location Details - Only show after selection */}
              {selectedLocation && markerPosition && (
                <Box sx={{ p: 2, flex: 1, overflow: "auto" }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                    Selected Location
                  </Typography>
                  
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: 2,
                      bgcolor: "info.light",
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <DragIndicator fontSize="small" />
                      <Typography variant="caption" color="text.secondary">
                        Drag the marker to refine your location
                      </Typography>
                    </Box>
                  </Paper>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                      FULL ADDRESS
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedLocation.formattedAddress}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                        LATITUDE
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {selectedLocation.lat.toFixed(4)}° N
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                        LONGITUDE
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {selectedLocation.lng.toFixed(4)}° W
                      </Typography>
                    </Box>
                  </Box>

                  {selectedLocation.addressComponents && (
                    <>
                      {selectedLocation.addressComponents.neighborhood && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                            CONTEXT
                          </Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            <Chip
                              label={`Neighborhood: ${selectedLocation.addressComponents.neighborhood}`}
                              size="small"
                              variant="outlined"
                            />
                            {selectedLocation.placeId && (
                              <Chip
                                label={`Place ID: ${selectedLocation.placeId.substring(0, 20)}...`}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      )}

                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                          ADDRESS COMPONENTS
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          {selectedLocation.addressComponents.streetNumber && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Street Number:
                              </Typography>
                              <Typography variant="body2">
                                {selectedLocation.addressComponents.streetNumber}
                              </Typography>
                            </Box>
                          )}
                          {selectedLocation.addressComponents.route && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Route:
                              </Typography>
                              <Typography variant="body2">
                                {selectedLocation.addressComponents.route}
                              </Typography>
                            </Box>
                          )}
                          {selectedLocation.addressComponents.city && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                City:
                              </Typography>
                              <Typography variant="body2">
                                {selectedLocation.addressComponents.city}
                              </Typography>
                            </Box>
                          )}
                          {selectedLocation.addressComponents.state && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                State:
                              </Typography>
                              <Typography variant="body2">
                                {selectedLocation.addressComponents.state}
                              </Typography>
                            </Box>
                          )}
                          {selectedLocation.addressComponents.country && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Country:
                              </Typography>
                              <Typography variant="body2">
                                {selectedLocation.addressComponents.country}
                              </Typography>
                            </Box>
                          )}
                          {selectedLocation.addressComponents.postalCode && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Postal Code:
                              </Typography>
                              <Typography variant="body2">
                                {selectedLocation.addressComponents.postalCode}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </>
                  )}

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <CheckCircle color="success" fontSize="small" />
                    <Typography variant="caption" color="text.secondary">
                      Accuracy: Precise
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Action Buttons */}
              <Box
                sx={{
                  p: 2,
                  borderTop: 1,
                  borderColor: "divider",
                  bgcolor: "white",
                  display: "flex",
                  gap: 1,
                }}
              >
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleClose}
                >
                  Discard
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleConfirm}
                  disabled={!selectedLocation}
                  startIcon={<CheckCircle />}
                >
                  Confirm Selection
                </Button>
              </Box>
            </Box>

            {/* Right Side - Map */}
            <Box sx={{ flex: 1, position: "relative" }}>
              <GoogleMap
                mapContainerStyle={{
                  width: "100%",
                  height: "100%",
                }}
                center={mapCenter}
                zoom={markerPosition ? 15 : 10}
                onLoad={onLoad}
                onUnmount={onUnmount}
                options={{
                  zoomControl: true,
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: true,
                }}
              >
                {markerPosition && isLoaded && googleWindow.google && (
                  <Marker
                    position={markerPosition}
                    draggable={true}
                    onDragEnd={onMarkerDragEnd}
                    icon={{
                      url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`
                        <svg width="40" height="50" viewBox="0 0 40 50" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                              <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
                            </filter>
                          </defs>
                          <path d="M20 0C8.954 0 0 8.954 0 20c0 12.5 20 30 20 30s20-17.5 20-30C40 8.954 31.046 0 20 0z" 
                                fill="#FF0000" 
                                stroke="#FFFFFF" 
                                stroke-width="2"
                                filter="url(#shadow)"/>
                          <circle cx="20" cy="20" r="6" fill="#FFFFFF"/>
                          <text x="20" y="45" font-family="Arial, sans-serif" font-size="8" font-weight="bold" fill="#FFFFFF" text-anchor="middle">TARGET</text>
                        </svg>
                      `),
                      scaledSize: new googleWindow.google.maps.Size(40, 50),
                      anchor: new googleWindow.google.maps.Point(20, 50),
                    }}
                    animation={googleWindow.google.maps.Animation.DROP}
                    title="Drag to adjust location"
                  />
                )}
              </GoogleMap>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
    </LoadScript>
  );
};

export default MapLocationPicker;
export type { LocationDetails, AddressComponents, MapLocationPickerProps };

